import * as React from 'react';

import { Button } from './button';
import { cn } from './lib/cn';
import { Modal } from './modal';
import { ReportActionRow } from './report-action-row';

/**
 * The CSP the report route serves, restated for the frame. `sandbox` is omitted
 * because CSP's `sandbox` directive is header-only — the iframe's `sandbox=""`
 * attribute is what provides it here, and it cannot be stripped in transit.
 */
const REPORT_FRAME_CSP =
  "default-src 'none'; style-src 'unsafe-inline'; img-src data:; form-action 'none'; base-uri 'none'";

const CSP_META = `<meta http-equiv="Content-Security-Policy" content="${REPORT_FRAME_CSP}">`;

/**
 * A doctype at the very start of the document, and nothing else. Anchored with
 * `^` so it can only ever match the report's OWN leading doctype — never a
 * `<!doctype>` mentioned inside a comment, an attribute or a `<pre>` block later
 * on. A leading BOM and leading whitespace are skipped because a parser skips
 * them too. `[^>]*>` matches the HTML tokenizer: a DOCTYPE token ends at the first
 * `>`, even one inside a quoted public/system identifier.
 */
const LEADING_DOCTYPE = /^[\uFEFF\s]*<!doctype[^>]*>/i;

/**
 * Prepend the report's own CSP to the markup handed to the frame. Purely
 * ADDITIVE — nothing in the report is removed, rewritten or sanitised, so this is
 * not an allow-list sanitiser standing in as the security boundary. It exists
 * because the markup reaches the frame through `srcdoc`, and a `srcdoc` document
 * inherits the embedding page's policy rather than the response header that came
 * with the bytes: on this path the meta is the ONLY thing enforcing „issues no
 * external network requests" (see the {@link SandboxedHtmlViewer} docblock).
 *
 * The insertion point is DETERMINISTIC and is never pattern-matched against the
 * report's markup: immediately after a leading doctype when there is one,
 * otherwise at the very front. Matching an anchor anywhere in the document — the
 * previous approach — is unsound on markup we did not author: `/<head[^>]*>/i`
 * also matches `<header …>`, so a head-less report placed the meta in the BODY,
 * where a CSP pragma is INERT; and a leading `<!-- <head> -->` buried it inside a
 * comment. Both failed silently, with `sandbox=""` (which cannot block network
 * requests) left as the only remaining layer.
 *
 * Why the front is safe and sufficient:
 *
 * - A `<meta>` arriving before `<html>`/`<head>` is reparented by the parser into
 *   the implied `<head>`, so the pragma is processed and enforced.
 * - Being FIRST is what matters. Multiple policies compose as a conjunction —
 *   every one of them must allow a request — so an attacker-authored
 *   `<meta http-equiv="Content-Security-Policy">` further down can only narrow
 *   ours, never widen it. Ours preceding every other byte additionally means no
 *   fetch in the document can start before it applies.
 * - With no doctype the document is quirks-mode either way, so prepending costs
 *   nothing; and for the doctype-after-a-comment shape, an `iframe srcdoc`
 *   document is explicitly exempted from the "token before the DOCTYPE ⇒ quirks
 *   mode" rule, so rendering is unchanged there too.
 */
export function withReportCsp(html: string): string {
  const doctype = LEADING_DOCTYPE.exec(html);
  const at = doctype === null ? 0 : doctype[0].length;

  return `${html.slice(0, at)}${CSP_META}${html.slice(at)}`;
}

/**
 * The inline frame's box when the caller does not choose one. Kept as the
 * default so neither existing call site changes appearance.
 */
const DEFAULT_INLINE_FRAME_CLASS = 'h-[480px] w-full';

/**
 * The ONLY place a report frame is constructed — the inline preview and the
 * expanded dialog both render through here, so the sandbox, the referrer policy
 * and the {@link withReportCsp} splice cannot diverge between the two surfaces.
 * A second frame written alongside this one would be a second place to forget
 * them, which is exactly what {@link SandboxedHtmlViewer}'s contract forbids.
 *
 * `className` styles the BOX only. It reaches `cn` after the base classes and so
 * can change the frame's size, radius and border — it can never reach `srcDoc`,
 * `sandbox` or `referrerPolicy`, all three of which are written here and are not
 * parameters.
 */
function ReportFrame({
  html,
  title,
  className,
  tabIndex,
}: {
  readonly html: string;
  readonly title: string;
  readonly className: string;
  readonly tabIndex?: number;
}) {
  return (
    <iframe
      title={title}
      srcDoc={withReportCsp(html)}
      sandbox=""
      referrerPolicy="no-referrer"
      tabIndex={tabIndex}
      className={cn('rounded-md border border-border bg-white', className)}
    />
  );
}

export type SandboxedHtmlViewerProps = {
  /** The report's own markup, exactly as it was stored. Policed here, not by the caller. */
  readonly html: string;
  /** The frame's accessible name — the only handle a test or a screen reader has on it. */
  readonly title: string;
  /** Classes for the wrapper around the frame and its affordance (margins, spacing). */
  readonly className?: string;
  /**
   * The INLINE frame's box, as an explicit prop rather than through `className`.
   * `cn()`'s merge order made a caller's `h-` / `w-` override order-dependent —
   * it might win, it might not — which is what left the hardcoded box unfixable
   * from outside (IMM-609). Defaults to `h-[480px] w-full`.
   */
  readonly frameClassName?: string;
  /**
   * `-1` keeps the INLINE frame out of a surrounding dialog's initial focus and tab cycle. Once focus is inside a
   * `sandbox=""` document every keystroke, Escape included, is delivered there, so a `Modal` whose first focusable
   * is the frame cannot be closed from the keyboard. Omitted, the frame is focusable as before.
   */
  readonly frameTabIndex?: number;
  /**
   * Label for the expand affordance _(DE: „Vollbild")_, supplied by the caller
   * because `packages/ui-kit` has no i18n dependency. Omitting it renders the
   * inline preview alone, so no existing caller gains an affordance it did not
   * ask for.
   */
  readonly expandLabel?: string;
  /** Accessible name for the expanded dialog's close button _(DE: „Schließen")_. */
  readonly closeLabel?: string;
  /**
   * The caller's own actions for this report _(DE: „Herunterladen")_, rendered in
   * the SAME row as the expand affordance and after it, so every action for one
   * report sits in one place above the frame (IMM-640.1).
   *
   * Rendered OUTSIDE {@link ReportFrame}, so — like every other prop here — it
   * cannot reach `srcDoc`, `sandbox` or the CSP splice.
   *
   * Ignored, with a dev-time warning, when `expanded` is supplied — see
   * {@link SandboxedHtmlViewerProps.expanded}.
   */
  readonly actions?: React.ReactNode;
  /**
   * Controlled fullscreen state. When supplied, the viewer renders no internal
   * action row — the caller owns both „Vollbild" and „Herunterladen" together, so
   * they can be placed anywhere without re-splitting the HTML report's actions
   * (IMM-828.1). Omitted, the viewer behaves exactly as today.
   *
   * `expandLabel` and `actions` are ignored (dev-time warning) when this is set.
   */
  readonly expanded?: boolean;
  /**
   * Reports every change to the controlled fullscreen state. Required in practice
   * whenever `expanded` is supplied — without it the dialog can be opened and
   * never closed — but kept optional in the type so the props stay a single type
   * literal (see the component's own docblock).
   */
  readonly onExpandedChange?: (expanded: boolean) => void;
};

/**
 * Render authored HTML we did not write, inside a frame that can neither script
 * nor reach the network (IMM-475.3, moved here by IMM-598 so both the customer and
 * the operator app render a Wertgutachten through the SAME boundary).
 *
 * The markup is handed to the frame as `srcdoc`, and a `srcdoc` document inherits
 * the EMBEDDING page's policy rather than the response header that came with the
 * bytes — so the route's `Content-Security-Policy` header does not govern this
 * frame. What is enforced here is (a) `sandbox=""` — EMPTY, so every capability is
 * withheld: no scripts, no plugins, no forms, and an opaque origin — which cannot
 * block network requests, and (b) the `<meta http-equiv>` CSP that
 * {@link withReportCsp} splices in, which is the ONLY thing denying an external
 * request.
 *
 * The splice happens INSIDE this component precisely so that rendering unpoliced
 * markup is not expressible: a caller cannot forget it, and there is no prop that
 * would let them opt out. That holds across BOTH surfaces this component renders
 * — the inline preview and the expanded dialog — because both go through the one
 * {@link ReportFrame}; none of the props below can suppress the splice, widen
 * `sandbox=""`, or supply markup that reaches the frame by another route.
 *
 * Two surfaces, because a Wertgutachten is a document and the inline box is a
 * card column (IMM-609). The inline frame stays the at-a-glance confirmation
 * that a report is there; `expandLabel` adds a way to actually READ it, on a
 * `Modal` sized `full` — viewport-relative, since the report's own layout width
 * is externally authored and no bounded card can be guaranteed to fit it. The
 * dialog is the shared `Modal`, never a hand-rolled overlay, so dismissal, focus
 * restore, the nested-Escape stack and backdrop behaviour come for free.
 */
export function SandboxedHtmlViewer({
  html,
  title,
  className,
  frameClassName,
  frameTabIndex,
  expandLabel,
  closeLabel,
  actions,
  expanded,
  onExpandedChange,
}: SandboxedHtmlViewerProps) {
  const controlled = expanded !== undefined;
  const [selfExpanded, setSelfExpanded] = React.useState(false);
  const isExpanded = expanded ?? selfExpanded;

  /*
   * Renderability, never `actions !== undefined` (BL-019). `ReactNode` includes
   * `false`, and `actions={readable && <X/>}` — which the operator app writes —
   * evaluates to exactly that on the negative branch: the guard would pass and the
   * row would render empty above the frame. `toArray` drops booleans, `null` and
   * `undefined`; the ORIGINAL `actions` is what gets rendered, since `toArray`
   * re-keys and would remount a stateful child.
   */
  const hasActions = React.Children.toArray(actions).length > 0;

  if (controlled && process.env.NODE_ENV !== 'production') {
    if (onExpandedChange === undefined) {
      console.error('SandboxedHtmlViewer: `expanded` without `onExpandedChange` leaves the dialog unclosable.');
    }
    if (expandLabel !== undefined || hasActions) {
      console.error(
        'SandboxedHtmlViewer: `expandLabel` and `actions` are ignored when `expanded` is controlled — the caller owns the row.',
      );
    }
  }

  const setExpanded = (next: boolean): void => {
    if (controlled) {
      onExpandedChange?.(next);
    } else {
      setSelfExpanded(next);
    }
  };

  return (
    <div className={cn('flex flex-col', className)}>
      {/*
       * The row follows its CONTENTS, not one particular occupant. Keying it on
       * `expandLabel` alone — as it was before IMM-640.1 added the slot — would
       * silently drop a caller's actions on a viewer with no expand affordance,
       * with no type error and no runtime complaint.
       *
       * Suppressed entirely in controlled mode: the caller owns both controls
       * together, outside this component (IMM-828.1).
       */}
      {!controlled && (expandLabel !== undefined || hasActions) && (
        <ReportActionRow>
          {expandLabel !== undefined && (
            <Button type="button" variant="secondary" size="sm" onClick={() => setExpanded(true)}>
              {expandLabel}
            </Button>
          )}
          {actions}
        </ReportActionRow>
      )}

      <ReportFrame
        html={html}
        title={title}
        className={frameClassName ?? DEFAULT_INLINE_FRAME_CLASS}
        {...(frameTabIndex === undefined ? {} : { tabIndex: frameTabIndex })}
      />

      {(controlled || expandLabel !== undefined) && (
        <Modal
          open={isExpanded}
          onClose={() => setExpanded(false)}
          size="full"
          title={title}
          // Spread rather than `closeLabel={closeLabel}`: under
          // `exactOptionalPropertyTypes` an explicit `undefined` is not the same
          // as an absent prop, and passing it would defeat `Modal`'s own default.
          {...(closeLabel === undefined ? {} : { closeLabel })}
          // A read-only report has nothing to lose, so Escape and the backdrop
          // dismiss outright rather than raising the "discard changes?" step.
          confirmOnClose={false}
          dismissOnBackdrop
          // Edge-to-edge: the default `px-6 py-6` would eat 48px of the width the
          // `full` size just bought. `flex` makes the frame a stretched flex item,
          // so it fills the body's height without depending on a percentage
          // resolving against a flex-sized ancestor.
          bodyClassName="flex p-0"
        >
          {/*
           * `tabIndex={-1}` keeps the frame OUT of the dialog's initial focus and
           * its tab cycle. A `sandbox=""` document is unreachable from this page,
           * so once focus is inside it every keystroke — Escape included — is
           * delivered there and never reaches the Modal's handler: landing focus
           * on the frame would leave the dialog unclosable from the keyboard.
           * Focus falls back to the card, so Escape and Tab-to-close both work,
           * and a reader who wants to scroll with the keyboard can still click
           * into the report deliberately.
           */}
          <ReportFrame html={html} title={title} tabIndex={-1} className="h-full w-full rounded-none border-0" />
        </Modal>
      )}
    </div>
  );
}
